import { useEffect, useState } from 'react';

import { useEditableFieldsContext } from 'modules/templateEditor/context/EditableFieldsContext';
import { useSaveTemplate } from 'modules/templateEditor/hooks/useSaveTemplate';
import { ConsumingApp } from '../../InStudioTemplateEditing.types';
import { useInStudioTakeoverContext } from '../../context/InStudioTakeoverContext';
import { UnsavedChangesModal } from './UnsavedChangesModal';

export enum UnsavedChangesModals {
  None = 'none',
  React = 'react',
}

type InStudioUnsavedChangesPromptsProps = { consumingApp?: ConsumingApp };

export const InStudioUnsavedChangesPrompts = ({
  consumingApp,
}: InStudioUnsavedChangesPromptsProps) => {
  const [openModal, setOpenModal] = useState<UnsavedChangesModals>(UnsavedChangesModals.None);
  const { isCheckingForUnsavedChanges, setShouldCloseTakeover, setCheckForUnsavedChanges } =
    useInStudioTakeoverContext();
  const { saveTemplate } = useSaveTemplate();
  const { isDirty, resetLiveTemplate } = useEditableFieldsContext();
  const isCampaignView = consumingApp === ConsumingApp.CAMPAIGN_EDITOR;

  const handleCancel = () => {
    setOpenModal(UnsavedChangesModals.None);
    setCheckForUnsavedChanges(false);
  };

  const handleLeaveWithoutSaving = () => {
    setOpenModal(UnsavedChangesModals.None);
    setShouldCloseTakeover(true);

    /** On campaign page, reset dirty `liveTemplate` form values
     * to render accurate preview when closing template takeover. */
    if (isCampaignView) resetLiveTemplate();
  };

  const handleSaveAndExit = () => {
    setOpenModal(UnsavedChangesModals.None);
    saveTemplate();
  };

  // prompt user about unsaved changes with modal
  useEffect(() => {
    if (isCheckingForUnsavedChanges && isDirty) {
      setOpenModal(UnsavedChangesModals.React);
    }
  }, [isCheckingForUnsavedChanges, isDirty]);

  // prompt user about unsaved changes with browser dialog (navigating outside of react-router)
  useEffect(() => {
    if (!isDirty) {
      return;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();

      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  return openModal === UnsavedChangesModals.React ? (
    <UnsavedChangesModal
      onClose={handleCancel}
      onLeave={handleLeaveWithoutSaving}
      onSave={handleSaveAndExit}
    />
  ) : null;
};
